<script lang="ts" setup>
    import { Link } from "@inertiajs/vue3";
    import { computed, ref, inject, onMounted, watch } from "vue";
    import type { PropType } from "vue";
    import { type Link as LinkType } from "@interfaces/Link";
    // import { route } from "ziggy-js";
    import { isEditing, isPreviewing } from "@builder.io/sdk-vue";
    import Cms from "@/components/Cms.vue";
    import { registeredComponents } from "@root/mega-menu-init";
    import { vElementHover } from "@vueuse/components";
    import { useDebounceFn, useEventListener, useElementVisibility, useMouseInElement } from '@vueuse/core'
    import { useTypeAheadStore } from "@/store/typeAhead";
    import RothcoCta from "@/components/RothcoCta.vue";
    import { getCtaLink } from "@/helpers/interfaces/Cta";
    import { slugify } from "@/helpers/composables/slugify";
    import useImageOptimization from "@/helpers/composables/useImageOptimization";
    import BlackArrowRight from "@icons/arrow-black-right.svg";
    let route = inject("route");
    let props = defineProps({
        menuItem: {
            type: Object as PropType<LinkType>,
            required: true
        }
    });
    let bannerData = ref({
        title: "",
        image: "",
        slug: "",
        cta: {
            label: null,
            ariaLabel: null,
            urlType: null,
            generalUrl: {
                value: {
                    data: {}
                }
            },
            productUrl: {},
            externalUrl: null
        }
    });
    let hoveredBanner = ref(props.menuItem.text);

    let hasChildren = computed(() => {
        return props.menuItem.secondary?.length > 0;
    });

    let showPallete = computed(() => {
        return props.menuItem.mainSection?.showPallete;
    });

    let hasMainLinks = computed(() => {
        return props.menuItem.mainSection?.links?.length > 0;
    });

    let mainLinks = computed(() => {
        return props.menuItem.mainSection?.links ?? [];
    });
    function getLinkUrl({ url, urlParams=[] }) {
        let newUrl = "/404";
        if(typeof url === 'string'){
            newUrl = url;
            try{
                return route(newUrl, urlParams);
            }catch(e){
                return newUrl;
            }
        }
        else{
            if (url?.urlType === "general") {
                newUrl = url?.generalUrl?.value?.data;
                if (newUrl?.alias) {
                    newUrl = route(newUrl.alias, newUrl.params);
                }else{
                    newUrl = "/404";
                }
            } else if (url?.urlType === "product") {
                newUrl = "/404";
                if(url?.productUrl?.productType === "item") {
                    newUrl = route("productDetail.show", { seo_name: url?.productUrl?.seoName });
                    
                }else if(url?.productUrl?.productType === "category") {
                    newUrl = route(`category.${url?.productUrl?.shopAll ? 'shop-all' : 'show'}`, { seo_name: url?.productUrl?.seoName });
                }else if(url?.productUrl?.productType === "collection") {
                    newUrl = route(`productListing.collection`, { collection: url?.productUrl?.seoName });
                }
            } else {
                newUrl = url?.externalUrl ?? "/404";
            }
        }
        return newUrl;
    }

    let editing = computed(() => {
        if (typeof window === "undefined") {
            return false;
        }
        return (
            (isEditing() || isPreviewing()) &&
            window.location.href.includes(
                `/banner/${slugify(props.menuItem.text)}`
            )
        );
    });

    let subMenuEditing = computed(()=>{
        let subMenuMap = new Map();
        if (typeof window === "undefined") {
            return subMenuMap;
        }
        for(let subMenu of props.menuItem.secondary){
            let editingSubMenu = (
                (isEditing() || isPreviewing()) &&
                window.location.href.includes(
                    `/banner/${slugify(props.menuItem.text)}/${slugify(subMenu.text)}`
                )
            )
            subMenuMap.set(subMenu.text, editingSubMenu  );
        }
        return subMenuMap
    });

    const isHoveringMainMenu = ref(false);
    let cmsLazy = ref(editing.value ? false : true);

    function onHover({isHovering=false, bannerText="", isMainMenu=false}) {
        if(isMainMenu){
            if(isHovering){
                forceHideDropdown.value = false;
                isHoveringMainMenu.value = true;
            }else{
                unHoveredMainMenu();
            } 
        }
        // cmsLazy.value = false;
        closeTypeAhead();
        blurTypeAhead();
        if(!isEditing() && !isPreviewing()){
            hoveredBanner.value = (!isHovering && !isMainMenu) ? props.menuItem.text : bannerText;
        }
    }

    const unHoveredMainMenu = useDebounceFn(() => {
        forceHideDropdown.value = false;
        isHoveringMainMenu.value = false;
    }, 100)

    let bannerName = computed(() => {
        if (hoveredBanner.value !== props.menuItem.text) {
            return `${slugify(props.menuItem.text)}/${slugify(hoveredBanner.value)}`;
        }
        return slugify(hoveredBanner.value);
    });

    let bannerUrl = computed(() => {
        if (`${bannerName.value}`) {
            return `/index/banner/${slugify(bannerName.value)}`;
        }
        return `/index/banner/${slugify(props.menuItem.text)}`;
    });

    let searchParams = computed(() => {

        let mainMenu = slugify(props.menuItem.text);
        let menusToViewFrom = [`${mainMenu}`];
        if (bannerName.value != mainMenu) {
            menusToViewFrom.push(`${bannerName.value}`);
        }
        return {
            'data.slug.$in': menusToViewFrom
        };
    });

    let bannerImage = computed(() => {
        return bannerData.value?.image;
    });

    let setBannerData = (data) => {
        if (
            data.slug === `${bannerName.value}` ||
            data.slug === `/${slugify(props.menuItem.text)}` ||
            (data.cascade === true && bannerName.value.includes(data.slug))
        ) {
            bannerData.value = data;
            return;
        }
        bannerData.value = {
            image: "",
            slug: `${slugify(props.menuItem.text)}`,
            cta: {
                label: null,
                ariaLabel: null,
                urlType: null,
                generalUrl: {
                    value: {
                        data: {}
                    }
                },
                productUrl: {},
                externalUrl: null
            }
        };
    };

    let typeAheadStore = useTypeAheadStore();
    let { close: closeTypeAhead, blurTypeAhead } = typeAheadStore;

    const subMenu = ref(null);
    const navSubMenu = ref(null);
    const forceHideDropdown = ref(false);
    const subMenuDropdownIsVisible = useElementVisibility(navSubMenu);

    onMounted(()=>{
        useEventListener(subMenu, 'click', (e)=>{
            if(e?.target?.closest("a")){
                forceHideDropdown.value = true;
            }
        })
    })
    
    let { optimize } = useImageOptimization(); 
    let mainMenuLink = ref(null);

    const { isOutside, elementPositionX, elementPositionY, x, y, elementHeight, elementWidth } = useMouseInElement(mainMenuLink);


    watch([x, y], ([newX, newY]) => {
        if(isOutside.value){
            // check to see how far the mouse is from the element. If it is within 50px, turn cmsLazy back to true
            if ((Math.abs(newX - elementPositionX.value) < elementWidth.value + 50 && Math.abs(newY - elementPositionY.value) < elementHeight.value + 50) && cmsLazy.value) {
                cmsLazy.value = false;
            }
        }
    });
</script>
<template>
    <div v-if="!hasChildren && !menuItem.useCmsMegaMenu" class="nav-main-item">
    <Link
        
        :href="getLinkUrl(menuItem)"
        :class="
        {
            'headline-l': true,
            'sale-nav-item': menuItem.sales
        }"
        >{{ menuItem.text }}</Link
    >
    </div>
    <div
        ref="subMenu"
        v-else
        :class="{
            'nav-main-item': true,
            hovered: editing || isHoveringMainMenu
        }"
    >
        <Link
            ref="mainMenuLink"
            v-element-hover="($event)=>(onHover({isHovering: $event, bannerText: menuItem.text, isMainMenu: true}))"
            :href="getLinkUrl(menuItem)"
            :class="{
                'headline-l': true,
                'sale-nav-item': menuItem.sales,
                'menu-hovered': subMenuDropdownIsVisible
            }"
            >{{ menuItem.text }}</Link
        >
        <div :class="{'nav-submenu': true, 'builder-editing': editing}" ref="navSubMenu" :style="forceHideDropdown ? 'display:none;' : ''">
            <div class="nav-submenu-container">
                <div
                    v-show="!menuItem.useCmsMegaMenu"
                    class="nav-submenu-level-2"
                >
                    <p class="headline-s">{{ menuItem.text }}</p>
                    <Link
                        v-if="menuItem.shopAllLink"
                        :href="menuItem.shopAllLink"
                        class="menu-cta-link"
                        >Shop All</Link
                    >
                    <ul v-if="hasMainLinks" class="nav-submenu-list">
                        <li
                            v-for="link of mainLinks"
                            class="nav-submenu-item"
                            :key="link"
                        >
                            <Link :href="getLinkUrl(link)" v-if="link.tag == null">{{
                                link.text
                            }}</Link>
                            <Link :href="getLinkUrl(link)" class="featured-item" v-else>
                                <p>{{ link.text }}</p>
                                <p class="featured-notation">
                                    {{ link.tag.value.data.tagname }}
                                </p>
                            </Link>
                        </li>
                    </ul>
                    <div class="links-palette" v-if="showPallete">
                        <Link 
                            v-bind="menuItem.palleteLinks ? menuItem.palleteLinks.military : {}" 
                            aria-label="Military category"
                        >
                            Military
                        </Link>
                        <Link 
                            v-bind="menuItem.palleteLinks ? menuItem.palleteLinks.tactical : {}" 
                            aria-label="Tactical category"
                        >
                            Tactical
                        </Link>
                        <Link 
                            v-bind="menuItem.palleteLinks ? menuItem.palleteLinks.work : {}" 
                            aria-label="Work category"
                        >
                            Work
                        </Link>
                        <Link 
                            v-bind="menuItem.palleteLinks ? menuItem.palleteLinks.outdoor : {}" 
                            aria-label="Outdoor category"
                        >
                            Outdoor
                        </Link>
                    </div>
                </div>
                <div class="nav-submenu-level-3" v-if="hasChildren && !menuItem.useCmsMegaMenu">
                    <ul class="nav-submenu-level-3-list">
                        <li
                            v-element-hover="($event)=>(onHover({isHovering: $event, bannerText: subMenuItem.text, isMainMenu: false}))"
                            v-for="subMenuItem of menuItem.secondary"
                            :class="{
                                'nav-submenu-item': true,
                                'nav-submenu-item-level-3': true,
                                hovered: subMenuEditing.get(subMenuItem.text)
                            }"
                            :key="subMenuItem.text"
                        >
                            <Link :href="getLinkUrl(subMenuItem)">{{
                                subMenuItem.text
                            }}</Link>
                            <BlackArrowRight
                                v-if="
                                    subMenuItem.tertiary &&
                                    subMenuItem.tertiary.length > 0
                                "
                                alt="arrow right"
                            />
                            <div
                                v-if="
                                    subMenuItem.tertiary &&
                                    subMenuItem.tertiary.length > 0
                                "
                                :class="{
                                    'nav-submenu-level-4': true,
                                    'nav-submenu': true,
                                }"
                            >
                                <ul class="nav-submenu-level-4-list">
                                    <li
                                        v-for="tertiaryMenuItem of subMenuItem.tertiary"
                                        class="nav-submenu-item nav-submenu-item-level-4"
                                        :key="tertiaryMenuItem.text"
                                    >
                                        <Link
                                            :href="getLinkUrl(tertiaryMenuItem)"
                                            >{{ tertiaryMenuItem.text }}</Link
                                        >
                                    </li>
                                    <li
                                        class="nav-submenu-item nav-submenu-item-level-4 nav-submenu-link"
                                    >
                                        <Link
                                            v-if="subMenuItem.shopAllLink"
                                            :href="subMenuItem.shopAllLink"
                                            class="menu-cta-link text-rothcoRed"
                                            >Shop All
                                            {{ subMenuItem.text }}</Link
                                        >
                                    </li>
                                </ul>
                            </div>
                        </li>
                    </ul>
                </div>
                <div class="nav-submenu-level-5" v-if="hasChildren && !menuItem.useCmsMegaMenu">
                    <RothcoCta v-if="bannerData.image" ctaType="link" v-bind="getCtaLink({url: bannerData.cta})" class="nav-menu-gradient" html-slotted>
                        <img :src="optimize(bannerData.image)" alt="" class="level-5-img" loading="lazy">
                        <p class="nav-submenu-image-title"></p>
                        <div class="nav-submenu-level-5-text">
                            <p class="nav-submenu-image-title">{{ bannerData.title }}</p>
                            <div v-if="bannerData.cta.label" class="nav-submenu-image-subtitle">
                                <span class="title-s">{{bannerData.cta.label}}</span>
                            </div>
                        </div>
                    </RothcoCta>
                    <Cms
                        v-show="false"
                        v-model:lazy="cmsLazy"
                        model="mega-menu-header"
                        :search-params="searchParams"
                        driver="builder"
                        is-section
                        :components="registeredComponents"
                        :key="bannerUrl"
                        @update:model-value="setBannerData"
                        :readable-fields="['slug', 'image', 'cta', 'title', 'cascade']"
                    />
                </div>
                <div v-else class="nav-submenu-level-3-w-fit w-full">
                    <Cms
                        v-model:lazy="cmsLazy"
                        model="mega-menu-header"
                        :search-params="searchParams"
                        driver="builder"
                        is-section
                        :components="registeredComponents"
                        :key="bannerUrl"
                        @update:model-value="setBannerData"
                        :readable-fields="['slug', 'image', 'cta', 'title', 'cascade']"
                    />
                </div>
            </div>
        </div>
    </div>
</template>
